/**
 * Responsive styles for Conversation UI
 * These styles adapt based on screen size while preserving desktop design
 */

export const getResponsiveStyles = (responsive) => {
  const { isMobile, isTablet, isDesktop, shouldShowMobileLayout, shouldShowDrawerSidebar } = responsive;
  
  return {
    // Main container responsive adjustments
    mainContainer: {
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      backgroundColor: '#fafafa',
      overflow: 'hidden',
      position: 'relative', // For mobile navigation
      // Mobile: Don't use padding, let flex handle layout
      ...(shouldShowMobileLayout && {
        paddingBottom: 0
      })
    },
    
    // Top Control Bar responsive
    topControlBar: {
      height: isMobile ? 56 : 64,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: isMobile ? '0 12px' : '0 24px',
      backgroundColor: '#ffffff',
      borderBottom: '1px solid rgba(0,0,0,0.08)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      zIndex: 100,
      gap: isMobile ? '8px' : '12px'
    },
    
    // Mode selector responsive
    modeSelector: {
      display: isMobile ? 'none' : 'flex', // Hide on mobile, use dropdown instead
      backgroundColor: '#f5f5f5',
      borderRadius: '12px',
      padding: '4px',
      gap: '4px'
    },
    
    // Mobile mode selector (dropdown)
    mobileModeSelector: {
      display: isMobile ? 'flex' : 'none',
      minWidth: '120px'
    },
    
    // Content area responsive
    contentArea: {
      display: 'flex',
      flex: 1,
      overflow: 'hidden',
      position: 'relative',
      flexDirection: shouldShowMobileLayout ? 'column' : 'row',
      // Mobile: Use flexbox instead of fixed heights
      ...(shouldShowMobileLayout && {
        flex: '1 1 auto',
        minHeight: 0 // Critical for flex children with overflow
      })
    },
    
    // Sidebar responsive
    sidebar: {
      width: shouldShowDrawerSidebar ? 0 : 300,
      backgroundColor: '#ffffff',
      borderRight: shouldShowDrawerSidebar ? 'none' : '1px solid rgba(0,0,0,0.08)',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      overflow: 'hidden',
      position: shouldShowDrawerSidebar ? 'fixed' : 'relative',
      zIndex: shouldShowDrawerSidebar ? 1200 : 50,
      // Drawer styles for mobile/tablet
      ...(shouldShowDrawerSidebar && {
        top: 0,
        left: 0,
        height: '100%',
        width: isMobile ? '85%' : '320px',
        maxWidth: '400px',
        transform: 'translateX(-100%)',
        boxShadow: '2px 0 8px rgba(0,0,0,0.15)'
      })
    },
    
    sidebarOpen: {
      transform: 'translateX(0)'
    },
    
    // Message area responsive
    messageArea: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#ffffff',
      position: 'relative',
      overflow: 'hidden',
      width: '100%', // Ensure full width on mobile
      // Mobile: Fill available space
      ...(shouldShowMobileLayout && {
        height: `calc(100vh - 56px - 60px - 56px)`, // viewport - topbar - inputbar - nav
        maxHeight: `calc(100vh - 56px - 60px - 56px)`
      })
    },
    
    messageContainer: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: isMobile ? '12px' : isTablet ? '16px' : '24px',
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? '12px' : '16px',
      scrollBehavior: 'smooth',
      WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
      // Mobile: Fill parent and scroll
      ...(shouldShowMobileLayout && {
        height: '100%',
        overflowY: 'scroll', // Force scroll on mobile
        '-webkit-overflow-scrolling': 'touch', // iOS momentum scrolling
        paddingBottom: '20px' // Extra space at bottom for better UX
      })
    },
    
    // Message bubble responsive
    messageBubble: {
      maxWidth: isMobile ? '85%' : isTablet ? '75%' : '70%',
      padding: isMobile ? '10px 12px' : '12px 16px',
      borderRadius: '16px',
      position: 'relative',
      animation: 'fadeInUp 0.3s ease-out',
      backgroundColor: '#f8f9fa',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      fontSize: isMobile ? '14px' : '15px',
      lineHeight: 1.5
    },
    
    // AI Suggestion Overlay responsive
    suggestionOverlay: {
      position: shouldShowMobileLayout ? 'fixed' : 'absolute',
      bottom: shouldShowMobileLayout ? 116 : 100, // Above input bar and mobile nav (60 + 56)
      left: shouldShowMobileLayout ? 0 : '50%',
      transform: shouldShowMobileLayout ? 'none' : 'translateX(-50%)',
      width: shouldShowMobileLayout ? '100%' : '90%',
      maxWidth: shouldShowMobileLayout ? '100%' : 600,
      backgroundColor: '#ffffff',
      borderRadius: shouldShowMobileLayout ? '16px 16px 0 0' : '16px',
      boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
      border: '1px solid rgba(255,152,0,0.2)',
      padding: isMobile ? '16px' : '20px',
      zIndex: 60,
      animation: shouldShowMobileLayout ? 'slideUpMobile 0.3s ease-out' : 'slideUp 0.3s ease-out'
    },
    
    // Input bar responsive
    inputBar: {
      display: 'flex',
      alignItems: 'center',
      padding: isMobile ? '12px' : isTablet ? '14px 20px' : '16px 24px',
      backgroundColor: '#ffffff',
      borderTop: '1px solid rgba(0,0,0,0.08)',
      gap: isMobile ? '8px' : '12px',
      minHeight: isMobile ? 60 : 80,
      flexShrink: 0, // Prevent shrinking
      position: 'relative',
      // Mobile: Fixed positioning
      ...(shouldShowMobileLayout && {
        position: 'fixed',
        bottom: 56, // Above mobile nav
        left: 0,
        right: 0,
        zIndex: 50
      })
    },
    
    // Input field responsive
    inputField: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#f8f9fa',
      borderRadius: isMobile ? '20px' : '24px',
      padding: isMobile ? '0 16px' : '0 20px',
      border: '2px solid transparent',
      transition: 'all 0.2s',
      minHeight: isMobile ? 40 : 44,
      '&:focus-within': {
        backgroundColor: '#ffffff',
        borderColor: '#FF8C00',
        boxShadow: '0 0 0 3px rgba(255,140,0,0.1)'
      }
    },
    
    // Text input responsive
    textInput: {
      flex: 1,
      border: 'none',
      backgroundColor: 'transparent',
      outline: 'none',
      fontSize: isMobile ? '16px' : '14px', // 16px prevents zoom on iOS
      padding: isMobile ? '10px 0' : '12px 0',
      color: '#1e293b'
    },
    
    // Send button responsive
    sendButton: {
      padding: isMobile ? '10px 20px' : '12px 24px',
      borderRadius: isMobile ? '20px' : '24px',
      border: 'none',
      fontWeight: 600,
      fontSize: isMobile ? '14px' : '14px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#ffffff',
      minHeight: isMobile ? 40 : 44,
      touchAction: 'manipulation' // Prevent double-tap zoom
    },
    
    // Icon button responsive (larger touch targets on mobile)
    iconButton: {
      width: isMobile ? 44 : 40,
      height: isMobile ? 44 : 40,
      minWidth: isMobile ? 44 : 40,
      borderRadius: '10px',
      border: 'none',
      backgroundColor: '#ffffff',
      color: '#64748b',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
      touchAction: 'manipulation',
      '&:hover': {
        backgroundColor: '#f1f5f9',
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
      },
      '&:active': {
        transform: 'translateY(0)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }
    },
    
    // Slide panel responsive
    slidePanel: {
      position: 'fixed',
      top: isMobile ? 0 : 64,
      right: 0,
      bottom: shouldShowMobileLayout ? 56 : 0, // Account for mobile nav
      width: isMobile ? '100%' : isTablet ? '60%' : 400,
      backgroundColor: '#ffffff',
      boxShadow: '-4px 0 16px rgba(0,0,0,0.1)',
      transform: 'translateX(100%)',
      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      zIndex: 200,
      display: 'flex',
      flexDirection: 'column'
    },
    
    // Mobile navigation bar (bottom)
    mobileNavigation: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      height: 56,
      backgroundColor: '#ffffff',
      borderTop: '1px solid rgba(0,0,0,0.08)',
      display: shouldShowMobileLayout ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'space-around',
      zIndex: 100,
      boxShadow: '0 -2px 8px rgba(0,0,0,0.1)'
    },
    
    mobileNavItem: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      color: '#64748b',
      cursor: 'pointer',
      transition: 'all 0.2s',
      gap: '2px',
      '&:active': {
        backgroundColor: 'rgba(0,0,0,0.05)'
      }
    },
    
    mobileNavItemActive: {
      color: '#FF8C00'
    },
    
    // Backdrop for mobile drawers/modals
    mobileBackdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.4)',
      zIndex: 1199,
      display: shouldShowDrawerSidebar ? 'block' : 'none',
      opacity: 0,
      visibility: 'hidden',
      transition: 'opacity 0.3s, visibility 0.3s',
      touchAction: 'none' // Prevent scrolling behind
    },
    
    mobileBackdropVisible: {
      opacity: 1,
      visibility: 'visible'
    },
    
    // Utility classes for mobile
    hideOnMobile: {
      display: isMobile ? 'none' : 'flex'
    },
    
    hideOnTablet: {
      display: isTablet ? 'none' : 'flex'
    },
    
    hideOnDesktop: {
      display: isDesktop ? 'none' : 'flex'
    },
    
    showOnlyOnMobile: {
      display: isMobile ? 'flex' : 'none'
    },
    
    showOnlyOnTablet: {
      display: isTablet ? 'flex' : 'none'
    },
    
    showOnlyOnDesktop: {
      display: isDesktop ? 'flex' : 'none'
    }
  };
};

// Mobile-specific animations
export const mobileAnimations = `
  @keyframes slideUpMobile {
    from {
      opacity: 0;
      transform: translateY(100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes slideInFromLeft {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

// Helper function to merge desktop and responsive styles
export const mergeResponsiveStyles = (desktopStyles, responsiveStyles) => {
  const merged = {};
  
  // Merge each style object
  Object.keys(desktopStyles).forEach(key => {
    if (responsiveStyles[key]) {
      // If responsive style exists, merge with desktop
      merged[key] = {
        ...desktopStyles[key],
        ...responsiveStyles[key]
      };
    } else {
      // Otherwise use desktop style
      merged[key] = desktopStyles[key];
    }
  });
  
  // Add any responsive-only styles
  Object.keys(responsiveStyles).forEach(key => {
    if (!merged[key]) {
      merged[key] = responsiveStyles[key];
    }
  });
  
  return merged;
};

export default getResponsiveStyles;